import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { siteNavigationFocusRing } from './SiteNavigationTab.style'

export enum SiteNavigationExpandableTabSizes {
  Normal = 'normal',
  Collapsed = 'collapsed',
}

const SiteNavigationExpandableTabParts = [
  'container',
  'headerContainer',
  'headerSelectionContainer',
  'headerIconWrapper',
  'headerIcon',
  'headerTitleText',
  'chevronIcon',
  'childrenContainer',
  'childItem',
  'childDot',
  'childText',
] as const

const helpers = createMultiStyleConfigHelpers(SiteNavigationExpandableTabParts)

export const SiteNavigationExpandableTabStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.125rem',
    },
    headerContainer: {
      display: 'flex',
      gap: '0.125rem',
    },
    headerSelectionContainer: {
      display: 'flex',
      height: '3rem',
      py: '0.625rem',
      userSelect: 'none',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      position: 'relative',
      gap: '0.125rem',
      textAlign: 'start',
      _hover: {
        bg: 'bg.muted',
      },
      _active: {
        bg: 'bg.emphasized',
      },
      _selected: {
        bg: 'bg.muted',
      },
      _focusVisible: siteNavigationFocusRing,
    },
    headerIconWrapper: {
      flex: 1,
      gap: '0.5rem',
      alignItems: 'center',
    },
    headerIcon: {
      boxSize: '1.5rem',
      color: 'fg.muted',
      _selected: {
        color: 'fg.info',
      },
    },
    headerTitleText: {
      ...textStyles[textStyleKeys.Body2],
      color: 'fg.muted',
      _selected: {
        color: 'fg.info',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
      marginBottom: 0,
    },
    chevronIcon: {
      boxSize: '1rem',
      color: 'fg.muted',
      transition: 'transform 0.2s ease',
      flexShrink: 0,
    },
    childrenContainer: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'max-height 0.2s ease',
    },
    childItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      height: '2.25rem',
      pl: '1.75rem',
      pr: '0.5rem',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      userSelect: 'none',
      textAlign: 'start',
      _hover: {
        bg: 'bg.muted',
      },
      _active: {
        bg: 'bg.emphasized',
      },
      _focusVisible: siteNavigationFocusRing,
    },
    childDot: {
      boxSize: '0.375rem',
      borderRadius: '50%',
      bg: 'fg.subtle',
      flexShrink: 0,
      '&[data-active="true"]': {
        bg: 'fg.info',
      },
    },
    childText: {
      ...textStyles[textStyleKeys.Body2],
      fontSize: '0.75rem',
      color: 'fg.muted',
      marginBottom: 0,
      '&[data-active="true"]': {
        color: 'fg.default',
        ...textStyles[textStyleKeys.Body2_Semibold],
        fontSize: '0.75rem',
      },
    },
  },
  sizes: {
    [SiteNavigationExpandableTabSizes.Normal]: {
      container: {
        alignSelf: 'stretch',
      },
      headerContainer: {
        flexDirection: 'column',
        alignSelf: 'stretch',
      },
      headerSelectionContainer: {
        alignItems: 'center',
        alignSelf: 'stretch',
        pl: '0.75rem',
        pr: '0.5rem',
      },
    },
    [SiteNavigationExpandableTabSizes.Collapsed]: {
      headerSelectionContainer: {
        width: '3rem',
        height: '3rem',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
      },
      childrenContainer: {
        display: 'none',
      },
      chevronIcon: {
        display: 'none',
      },
    },
  },
  defaultProps: {
    size: SiteNavigationExpandableTabSizes.Normal,
  },
})

export const useSiteNavigationExpandableTabStyle = (
  props?: StyleThemingProps,
) => {
  return mergeMultiStyleConfig(
    SiteNavigationExpandableTabStyle,
    props,
  ) as MultiStyleType<typeof SiteNavigationExpandableTabParts>
}
